import { Badge, Button, LayerCard } from '@cloudflare/kumo'
import { FilePdfIcon } from '@phosphor-icons/react'
import type { PrintJob } from '@hub-wifi/shared'
import { useTranslation } from '../../i18n'

interface Props {
  job: PrintJob
  expiresInLabel: string
  onCancel: (job: PrintJob) => void
  busy: boolean
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

type KumoBadgeVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'neutral'

const STATUS_VARIANT: Record<PrintJob['status'], KumoBadgeVariant> = {
  queued: 'secondary',
  releasing: 'primary',
  printing: 'primary',
  completed: 'success',
  failed: 'error',
  cancelled: 'neutral',
  expired: 'warning',
}

/**
 * One job card in the coworker queue: file icon, ellipsized name, status badge,
 * meta line and (queued/failed only) a 44px Cancel button. Printing itself is
 * released at the printer after scanning its QR, not from here.
 */
export function PrintJobRow({ job, expiresInLabel, onCancel, busy }: Props) {
  const { t } = useTranslation()
  const cancellable = job.status === 'queued' || job.status === 'failed'
  return (
    <LayerCard data-testid={`print-job-row-${job.id}`}>
      <LayerCard.Primary>
        <div className="flex items-center gap-3">
          <FilePdfIcon size={24} className="shrink-0 text-kumo-subtle" />
          <div className="min-w-0 flex-1 truncate font-medium">{job.fileName}</div>
          <Badge variant={STATUS_VARIANT[job.status]}>{t(`print.status.${job.status}`)}</Badge>
        </div>
        <div className="mt-1 flex items-center justify-between gap-3">
          <div className="min-w-0 text-sm text-kumo-subtle">
            {formatSize(job.sizeBytes)} · {new Date(job.uploadedAt).toLocaleString()}
            {cancellable && <> · {t('print.row.expiresIn', { in: expiresInLabel })}</>}
          </div>
          {cancellable && (
            <Button
              variant="secondary-destructive"
              disabled={busy}
              onClick={() => onCancel(job)}
              type="button"
              className="shrink-0"
              style={{ minHeight: 44 }}
            >
              {t('print.row.cancel')}
            </Button>
          )}
        </div>
      </LayerCard.Primary>
    </LayerCard>
  )
}
